import * as React from 'react';
import { IDailyResortLiftTickets } from '@/api/resort/types';
import CurrencyNotice from '@/components/common/CurrencyNotice';
import { DataTable } from '@/components/common/DataTable';
import { I18nKey } from '@/i18n/types';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { formatDateFromStr } from '@/util/formatDate';
import { passTypes, ticketsTypes, TicketsTypesEnum } from './constants';

// Daily lift-ticket pricing table. Rows = pass types (weekday, weekend,
// halfDay, twoDay, sixDay), columns = age groups. Both axes are filtered
// to only the entries that actually have a price in the API payload.

const ticketLabelKey: Record<TicketsTypesEnum, string> = {
  [TicketsTypesEnum.children]: 'child',
  [TicketsTypesEnum.teenager]: 'junior',
  [TicketsTypesEnum.adult]: 'adult',
  [TicketsTypesEnum.senior]: 'senior',
};

const ucFirst = (s: string) => s[0].toUpperCase() + s.substring(1);

// Narrowed lookup — the `resort` field on IDailyResortLiftTickets shares the
// type's keyof union, but we know the constructed key always points at a
// price string (or null), so cast the read site accordingly.
const readPrice = (d: IDailyResortLiftTickets, age: TicketsTypesEnum, pass: string) => (
  d[`${age}${ucFirst(pass)}Price` as keyof IDailyResortLiftTickets] as string | null
);

interface IProps {
  dailyLiftTickets: IDailyResortLiftTickets;
}

export const hasDailyTickets = (d: IDailyResortLiftTickets) => ticketsTypes
  .some((age) => passTypes.some((pass) => !!readPrice(d, age, pass)));

export const DailyTicketsTable = ({ dailyLiftTickets }: IProps) => {
  const { t } = useOTSTranslation('common');

  const activeAges = ticketsTypes.filter(
    (age) => passTypes.some((pass) => !!readPrice(dailyLiftTickets, age, pass)),
  );
  const activePasses = passTypes.filter(
    (pass) => activeAges.some((age) => !!readPrice(dailyLiftTickets, age, pass)),
  );

  if (!activeAges.length || !activePasses.length) return null;

  return (
    <>
      {dailyLiftTickets.currency && (
        <CurrencyNotice className="mb-3" currency={dailyLiftTickets.currency} />
      )}
      <DataTable minWidth={560}>
        <DataTable.Head>
          <DataTable.HeadRow>
            <DataTable.HeadCell>
              {`${t('resort.liftTickets.lastUpdated')} ${formatDateFromStr(dailyLiftTickets.lastModified || undefined, 'DD')}`}
            </DataTable.HeadCell>
            {activeAges.map((age) => {
              const labelKey = ticketLabelKey[age];
              const ageRange = dailyLiftTickets[`${age}Age` as keyof IDailyResortLiftTickets] as string | null;
              return (
                <DataTable.HeadCell key={age} align="right">
                  <span className="block">{t(`resort.liftTickets.${labelKey}` as I18nKey['common'])}</span>
                  {ageRange ? (
                    <span className="block text-xs font-normal normal-case opacity-70">{ageRange}</span>
                  ) : null}
                </DataTable.HeadCell>
              );
            })}
          </DataTable.HeadRow>
        </DataTable.Head>
        <DataTable.Body>
          {activePasses.map((pass, i) => (
            <DataTable.Row key={pass} zebra={i % 2 === 1}>
              <DataTable.RowHeader>
                {t(`resort.liftTickets.${pass}` as I18nKey['common'])}
              </DataTable.RowHeader>
              {activeAges.map((age) => (
                <DataTable.Cell key={age} align="right">
                  {readPrice(dailyLiftTickets, age, pass) || t('common.dash')}
                </DataTable.Cell>
              ))}
            </DataTable.Row>
          ))}
        </DataTable.Body>
      </DataTable>
    </>
  );
};
